/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useLazyQuery } from '@apollo/client';

import { posProductsQuery } from 'queries/Product';
import { useVenueContext } from 'routes/Main';
import { PosProduct, Product } from 'generated/graphql';
import { useCategoryContext } from 'components/CategoryList/CategoryContext';
import { UniqueIdentifier } from '@dnd-kit/core';
import { productResultToInlineSearchItem } from 'utils/ListItem';
import ItemInlineSearch from 'components/InlineSearch/ItemInlineSearch';
import { addAppsToProductVariantsByApp, sanitiseUrlParam } from 'utils';
import { useParams } from 'react-router-dom';
import { useAppContext } from '../../../../AppContext';

type Props = { currentCategoryId: UniqueIdentifier; currentCategoryItems: string[] };

export const InlineAddHandler: React.FC<Props> = ({ currentCategoryId, currentCategoryItems }) => {
  const { currentVenue } = useVenueContext();
  const { menuIndex, addNewInlineProduct } = useCategoryContext();

  const { draftMenus } = useAppContext();
  const { menuId } = useParams();
  const sanitisedMenuId = sanitiseUrlParam(menuId);
  const draftMenu = draftMenus?.find((menu) => menu.mmId === sanitisedMenuId);

  const [hasMore, setHasMore] = useState(false);
  const [results, setResults] = useState<{ product: Product | PosProduct; enabled: boolean; posPrice?: number }[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentOffset, setOffset] = useState(0);

  const [getPosProducts] = useLazyQuery(posProductsQuery);

  const handleItemAdd = (productIndex: number): void => {
    addNewInlineProduct(results[productIndex].product, currentCategoryId);
  };

  const allAppProducts = useMemo(() => {
    const response: { product: Product; enabled: boolean }[] = [];

    if (draftMenu) {
      response.push(
        ...draftMenu.products.map((product) => {
          return {
            product,
            enabled: !currentCategoryItems.some((posId) => {
              return product.customPosId ? posId === product.customPosId : posId === product.posId;
            })
          };
        })
      );
    } else if (menuIndex !== -1 && currentVenue?.menus && currentVenue.menus.length > menuIndex) {
      response.push(
        ...currentVenue.menus[menuIndex].products.map((product) => {
          return {
            product,
            enabled: !currentCategoryItems.some((posId) => {
              return product.customPosId ? posId === product.customPosId : posId === product.posId;
            })
          };
        })
      );
    }

    // add products from other menus
    currentVenue?.menus?.forEach((menu, index) => {
      if (index === menuIndex) return;

      menu.products.forEach((product) => {
        const productIfExists = response.some((loopedProduct) => loopedProduct.product.posId === product.posId);

        if (!productIfExists) {
          response.push({ product, enabled: true });
        }
      });
    });

    return response.sort((a, b) => a.product.name.localeCompare(b.product.name));
  }, [currentCategoryItems, currentVenue?.menus, draftMenu, menuIndex]);

  const getAppProductSearchResults = useCallback(
    (text: string) => {
      const formattedText = text.toLowerCase().trim();

      if (!formattedText) {
        return [];
      }
      return allAppProducts.filter((result) => {
        return (
          result.product.name.toLowerCase().includes(formattedText) ||
          result.product.posId.toLowerCase().includes(formattedText)
        );
      });
    },
    [allAppProducts]
  );

  const getInitialResults = useCallback(
    async (searchTerm: string) => {
      if (searchTerm.length < 2) {
        setResults([]);
        return;
      }
      setLoading(true);

      const menuHasSurcharge = draftMenu?.surcharge && draftMenu?.surcharge?.amount > 0;
      const appProductResults = getAppProductSearchResults(searchTerm);
      const {
        data: {
          posProducts: { items: posProducts, limit, count }
        }
      }: { data: { posProducts: { items: PosProduct[]; limit: number; offset: number; count: number } } } =
        await getPosProducts({
          variables: { locationId: currentVenue?.hashedId, searchTerm, offset: 0 }
        });
      setOffset(limit);
      if (!posProducts || count <= limit) setHasMore(false);
      else {
        setHasMore(true);
      }

      const posResults = posProducts.filter(
        (posProduct: PosProduct) =>
          !allAppProducts.map((appResult) => appResult.product.posId).includes(posProduct.posId)
      );

      const finalResults = [
        ...appProductResults.map((appProduct) => ({
          product: appProduct.product,
          enabled: appProduct.enabled,
          ...((menuHasSurcharge ||
            appProduct.product.variablesByApp.some((v) => v.surcharge && v.surcharge.amount > 0)) && {
            posPrice: posProducts.find((posProduct) => posProduct.posId === appProduct.product.posId)?.unitPrice
          })
        })),
        ...posResults.map((product) => ({ product, enabled: true }))
      ];

      setResults(finalResults);
      setLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allAppProducts, currentVenue?.hashedId, getAppProductSearchResults, getPosProducts]
  );
  const debouncedSearch = useDebouncedCallback(async (searchTerm) => await getInitialResults(searchTerm), 200);

  const searchMorePOSResults = useCallback(
    async (searchTerm: string, page: number): Promise<void> => {
      if (searchTerm.length < 2 || !results.length) return;
      const {
        data: {
          posProducts: { items: posProducts, limit, count }
        }
      }: { data: { posProducts: { items: PosProduct[]; limit: number; count: number } } } = await getPosProducts({
        variables: {
          locationId: currentVenue?.hashedId,
          searchTerm,
          offset: currentOffset
        }
      });
      const newOffset = currentOffset + limit;
      setOffset(newOffset);
      if (!posProducts || !posProducts.length) {
        setHasMore(false);
        return;
      }
      if (count <= newOffset) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
      const appProductResults = getAppProductSearchResults(searchTerm);

      const posResults = posProducts.filter(
        (posProduct: PosProduct) =>
          !appProductResults.map((appResult) => appResult.product.posId).includes(posProduct.posId)
      );
      setResults((results) => [
        ...results,
        ...posResults.map((posProduct) => ({ product: posProduct, enabled: true }))
      ]);
    },
    [currentOffset, currentVenue?.hashedId, getAppProductSearchResults, getPosProducts, results.length]
  );
  return (
    <div style={{ marginTop: '12px' }}>
      <ItemInlineSearch
        loading={loading}
        handleChange={(text) => debouncedSearch(text)}
        values={results.map((p) => productResultToInlineSearchItem(p.product, p.posPrice, p.enabled))}
        handleSubmit={handleItemAdd}
        handleLoadMore={searchMorePOSResults}
        setHasMore={setHasMore}
        hasMore={hasMore}
        initText="Enter item name"
        buttonText="+ Add Item"
      />
    </div>
  );
};
